import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "../../../src/core/api/axiosClient";

const initialState: any = {
  assetModels: [],
  isLoadingAssetModels: false,
  assetModelDetails: {},
  isLoadingAssetModelDetails: false,
  isLoading: false,
  error: null,
  assetModelDownload: [],
  isLoadingAssetModelDownload: false,
};

export const getAssetModels: any = createAsyncThunk(
  "content/getAssetModels",
  async ({ PageNumber, pageSize, searchText, SortBy, SortDirection }: any) => {
    const res = await axios.get(`/slpapp/AssetModel/getall`, {
      params: { PageNumber, pageSize, searchText, SortBy, SortDirection },
    });

    const data = await res.data;
    return data;
  },
);

export const getAssetModelsByCode: any = createAsyncThunk(
  "content/getAssetModelsByCode",
  async ({ assetModelCode }: any) => {
    const res = await axios.get(`/slpapp/AssetModel/AssetModelbycode`, {
      params: { assetModelCode },
    });

    const data = await res?.data?.data;
    return data;
  },
);

export const addAssetModel: any = createAsyncThunk(
  "content/addAssetModel",
  async ({ payload, file }: any) => {
    try {
      const formData = new FormData();

      formData.append("PictureFile", file);

      const res = await axios.post(`/slpapp/AssetModel`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          AssetModelCode: payload.assetModelCode,
          AssetTypeId: payload.assetTypeId,
          Description: payload.description,
          RatedWatt: payload.ratedWatt,
          SupplierId: payload.supplierId,
          ExpectedLifeTime: payload.expectedLifeTime,
          Remark: payload.remark,
        },
      });

      const data = await res?.data;
      return data;
    } catch (error) {
      console.error("Error uploading-file/ form sending:", error);
      throw error;
    }
  },
);

export const editAssetModel: any = createAsyncThunk(
  "content/editAssetModel",
  async ({ payload, assetModelCode, file }: any) => {
    try {
      const formData = new FormData();

      formData.append("PictureFile", file);

      const res = await axios.put(`/slpapp/AssetModel`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          AssetModelCode: assetModelCode,
          AssetTypeId: payload.assetTypeId,
          Description: payload.description,
          RatedWatt: payload.ratedWatt,
          SupplierId: payload.supplierId,
          ExpectedLifeTime: payload.expectedLifeTime,
          Remark: payload.remark,
        },
      });

      const data = await res.data;
      return data;
    } catch (error) {
      console.error("Error uploading-file/ form sending:", error);
      throw error;
    }
  },
);

export const deleteAssetModel: any = createAsyncThunk(
  "content/deleteAssetModel",
  async ({ assetModelCode }: any) => {
    const res = await axios.delete(`/slpapp/AssetModel`, {
      params: { assetModelCode },
    });

    const data = await res.data.data;
    return data;
  },
);

export const downloadAssetModelFile: any = createAsyncThunk(
  "content/downloadAssetModelFile",
  async ({
    fileType = "downloadPdf",
    tcCode = null,
    siteCode = null,
    blockCode = null,
    searchText = "",
    sortBy = null,
    sortDirection = "ASC",
  }: any) => {
    const payload = {
      tcCode,
      siteCode,
      blockCode,
      searchText,
      sortBy,
      sortDirection,
    };

    try {
      const response = await axios.post(
        `/slpapp/AssetModel/getAssetModel/${fileType}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
          responseType: "blob",
        },
      );

      let filename = response.headers["content-disposition"]
        .split(";")
        .find((n: any) => n.includes("filename="))
        .replace("filename=", "")
        .trim();

      let fileBlob;
      if (fileType === "downloadPdf") {
        fileBlob = new Blob([response.data], { type: "application/pdf" });
      } else {
        fileBlob = new Blob([response.data], {
          type: "application/application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }

      const fileUrl = URL.createObjectURL(fileBlob);

      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = filename;

      link.click();

      URL.revokeObjectURL(fileUrl);

      return response.data;
    } catch (error) {
      console.error("Error downloading fault report:", error);
      throw error;
    }
  },
);

export const assetModelSlice = createSlice({
  name: "assetModel",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAssetModels.pending, (state) => {
      state.isLoadingAssetModels = true;
    });
    builder.addCase(getAssetModels.fulfilled, (state, action) => {
      state.isLoadingAssetModels = false;
      state.assetModels = action.payload;
    });
    builder.addCase(getAssetModels.rejected, (state, action) => {
      state.isLoadingAssetModels = false;
      state.error = action.error.message;
    });

    builder.addCase(getAssetModelsByCode.pending, (state) => {
      state.isLoadingAssetModelDetails = true;
    });
    builder.addCase(getAssetModelsByCode.fulfilled, (state, action) => {
      state.isLoadingAssetModelDetails = false;
      state.assetModelDetails = action.payload;
    });
    builder.addCase(getAssetModelsByCode.rejected, (state, action) => {
      state.isLoadingAssetModelDetails = false;
      state.error = action.error.message;
    });

    builder.addCase(addAssetModel.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(addAssetModel.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(addAssetModel.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(deleteAssetModel.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteAssetModel.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(deleteAssetModel.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(editAssetModel.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(editAssetModel.fulfilled, (state, action) => {
      state.isLoading = false;
    });
    builder.addCase(editAssetModel.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message;
    });
    builder.addCase(downloadAssetModelFile.pending, (state) => {
      state.isLoadingAssetModelDownload = true;
    });
    builder.addCase(downloadAssetModelFile.fulfilled, (state, action) => {
      state.isLoadingAssetModelDownload = false;
      state.assetModelDownload = action.payload;
    });
    builder.addCase(downloadAssetModelFile.rejected, (state, action) => {
      state.isLoadingAssetModelDownload = false;
      state.error = action.error.message;
    });
  },
});

export default assetModelSlice.reducer;
